import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { MenuItem, MessageService, LazyLoadEvent } from 'primeng/api';
import {XpressUserService, AppDataService} from 'app/services';
import {XpressDeliveryPaymentMethodEnum, ServiceTypeEnum, GenderEnum, BetaFeature} from 'app/models';
import { Utils } from 'app/utils';
import * as moment from 'moment';
import {CampaignUsersService} from 'app/services/campaign-users.service';
import {PaymentProcessingService} from 'app/services/payment-processing.service';
import { TableLazyLoadEvent } from 'primeng/table';
import {SubscriptionService} from 'app/services/subscription.service';

enum CustomerDetailsTab {
    Details = 1,
    Finance = 2,
    OrderHistory = 3,
    Referrals = 4,
    Insights = 5,
    SupportTickets = 6
}

enum AdjustWalletBalanceTypeEnum {
    None = 0,
    AdminAdjustment = 1,
}

export interface SubscriptionProductDto {
    Id: number;
    Name: string;
    Description?: string;
    Price: number;
    ValidFrom: string;
    ValidUntil: string;
    ValidityPeriodDisplay: string;
    IsPurchased: boolean;
}

@Component({
    selector: 'customer-info',
    templateUrl: './customer-info.component.html',
    styleUrls: ['./customer-info.component.scss',
        '../../../../../styles/paginator.scss',
        '../../../../../styles/style-layout.scss'],
    providers: [MessageService]
})

export class CustomerInfoComponent implements OnInit {
    @Input()
    public showPopup: boolean = false;

    public customerId!: number;
    @Input()
    set _customerId(value: number) {
        this.customerId = value;
        if (this.customerId > 0) {
            this.getInfoUser();
        }
    }

    @Input()
    public showEditFields: boolean = false;

    // Customer info is embedded into order, finance and marketing screens as well; the diagnostic
    // log block belongs only to the support pages that opt in.
    @Input()
    public showLogsRequest: boolean = false;

    @Output() changedVisibleCustomerInfoEmit = new EventEmitter<boolean>();
    changedVisibleCustomerInfo(increased: any) {
        this.changedVisibleCustomerInfoEmit.emit(increased);
    }

    @Input()
    public isVisibleCustomerInfo: boolean = false;

    @Input() isBanned!: boolean;
    @Input() isSuspended: boolean = false;

    @Output() suspendSuccessEmit = new EventEmitter<void>();
    @Output() banSuccessEmit = new EventEmitter<void>();
    @Output() liftBanSuccessEmit = new EventEmitter<void>();
    @Output() liftSuspensionSuccessEmit = new EventEmitter<void>();

    public customer: any = undefined;

    public isVisibleDisplayOrderDetail: boolean = false;
    public isLiftBanAction: boolean = false;
    public isLiftSuspensionAction: boolean = false;

    // Grid settings
    public rowsPerPage: number = 15;
    public sortField: string | string[] = '';
    private sortOrder: number = 1;
    public totalRecords: number = -1;
    public first: number = 0;

    public locationsCols: any[] = [
        { field: 'Name', header: 'Name', width: '100px' },
        { field: 'Country', header: 'Country', width: '100px' },
        { field: 'City', header: 'City', width: '100px' },
        { field: 'Address', header: 'Address', width: '100px' },
        { field: 'IsSelected', header: 'Selected', width: '100px' },
        { field: 'IsDeleted', header: 'Deleted', width: '100px' }
    ];
    public transactionsCols: any[] = [
        { field: 'CreatedAt', header: 'Date/Time', width: '220px' },
        { field: 'TransactionTypeName', header: 'Transaction', width: '120px' },
        { field: 'Amount', header: 'Funding Source', width: '120px' },
        { field: 'IsPaid', header: 'Status', width: '100px' },
        { field: 'Amount', header: 'Value', width: '100px' },
        { field: 'Id', header: 'TX ID', width: '100px' }
    ];
    public ordersHistoryCols: any[] = [
        { field: 'CreatedAt', header: 'Date/Time', width: '100px' },
        { field: 'Service', header: 'Service', width: '100px' },
        { field: 'Status', header: 'Status', width: '100px' },
        { field: 'Value', header: 'Value', width: '100px' },
        { field: 'Id', header: 'TX ID', width: '100px' },
        { field: 'BookingId', header: 'Booking ID', width: '100px' },
    ];
    public refferalsCols: any[] = [
        { field: 'CreatedAt', header: 'Date/Time', width: '100px' },
        { field: 'UserName', header: 'Service', width: '100px' },
        { field: 'Type', header: 'Type', width: '100px' },
        { field: 'TotalSpend', header: 'Value', width: '100px' }
    ];
    public promosCols: any[] = [
        { field: 'Id', header: 'Id', width: '100px' },
        { field: 'Name', header: 'Name', width: '100px' },
        { field: 'Code', header: 'Code', width: '100px' },
        { field: 'Description', header: 'Description', width: '100px' },
    ];
    public scheduleCols: any[] = [
        { field: 'XpressDeliveryOrderId', header: 'Order Id', width: '100px' },
        { field: 'ScheduleTimeText', header: 'Next Schedule', width: '100px' },
        { field: 'VehicleTypeText', header: 'Vehicle Type', width: '100px' },
        { field: 'RepeatDaysOfWeek', header: 'Repeat Days Of Week', width: '100px' }
    ];

    showSpinner: boolean = true;
    isFinanceTransactionsLoading: boolean = false;
    userAdditionalInfo: any;
    searchTimer: number = 0;
    filterSearch: string = '';
    filterSearchDate: Date[] | undefined;

    displayModalSuspend: boolean = false;
    suspendUser: {
        userId: number | null;
        userName: string | null;
    } = { userId: null, userName: null };

    displayModalBanned: boolean = false;
    bannedUser: {
        userId: number | null;
        userName: string | null;
    } = { userId: null, userName: null };

    banReason: string = '';
    banNotes: string = '';
    suspendReason: string = '';
    suspendNotes: string = '';

    isVisibleCustomerTransactionDetail: boolean = false;
    isVisibleCustomerOrderDetail: boolean = false;
    transactionId!: number;
    orderTransactionId!: number;

    xpressDeliveryPaymentMethod = XpressDeliveryPaymentMethodEnum;

    filter: {
        transaction: XpressDeliveryPaymentMethodEnum | null;
        status: boolean | null;
    } = { transaction: null, status: null };

    serviceTypeList: any = [];
    selectedServiceTypes: ServiceTypeEnum[] = [];
    displayConfirmDialog: boolean = false;
    visibleCustomerManageDiscountDialog: boolean = false;
    displaySubscriptionConfirmDialog: boolean = false;

    hasAccessToAdjustWallet: boolean = false;
    // Named explicitly on purpose. BetaFeature.BetaFeatureList is a mixed catalog: it also carries flags that
    // describe an admin's own capabilities, and Disable2FA, which makes this customer's login OTP the constant
    // 112233 and stops the real one being sent. Only features that are safe to set on a customer belong here,
    // so this must not be replaced by that list.
    betaFeatureList = [
        { label: BetaFeature.WEEKLY_USER_WALLET_TOP_UP, value: BetaFeature.WEEKLY_USER_WALLET_TOP_UP },
        { label: BetaFeature.PRIORITY_ORDER, value: BetaFeature.PRIORITY_ORDER },
        { label: BetaFeature.HIGHER_LIMIT_PLACE_ORDER, value: BetaFeature.HIGHER_LIMIT_PLACE_ORDER },
        { label: BetaFeature.PAUSE_COMMERCE, value: BetaFeature.PAUSE_COMMERCE },
        { label: BetaFeature.RESUME_COMMERCE, value: BetaFeature.RESUME_COMMERCE },
        { label: BetaFeature.PAYMENT_GATEWAY_MAYA, value: BetaFeature.PAYMENT_GATEWAY_MAYA },
        { label: BetaFeature.PAYMENT_GATEWAY_XENDIT, value: BetaFeature.PAYMENT_GATEWAY_XENDIT }
    ];
    selectedBetaFeatures: string[] = [];

    adjustWalletBalanceDescription: string | null = null;
    adjustWalletBalanceType = [
        {
            Id: 0,
            Name: 'None'
        },
        {
            Id: AdjustWalletBalanceTypeEnum.AdminAdjustment,
            Name: 'Admin adjustment'
        }
    ];
    selectedAdjustWalletBalanceType: { Id: number; Name: string } | null = this.adjustWalletBalanceType[0];
    isValidAmount: boolean = true;
    adjustWalletBalanceAmount: number = 0;
    isRefundButtonDisabled: boolean = false;
    isRefundApplyButtonDisabled: boolean = false;
    showDialogRefundTransaction: boolean = false;
    adjustWalletBalanceIdempotencyKey: string = '';
    private isAdjustWalletBalanceInFlight: boolean = false;
    private isTransactionsRequestInFlight: boolean = false;
    private lastTransactionsRequestKey: string = '';

    constructor(
        private campaignUsersService: CampaignUsersService,
        private messageService: MessageService,
        private xpressUserService: XpressUserService,
        public appDataService: AppDataService,
        private paymentProcessingService: PaymentProcessingService,
        private subscriptionService: SubscriptionService,
    ) {
        this.hasAccessToAdjustWallet =
            this.appDataService.betaFeaturesIsExist([BetaFeature.ADJUST_WALLET]);
    }

    ngOnInit(): void {
        this.addServiceTypeInToList();
        this.getProducts();
    }

    subscriptionProducts: SubscriptionProductDto[] = [];
    selectedSubscriptionProduct: SubscriptionProductDto | null = null;
    xpressSubscriptionReceiptNumber: string = '';

    getProducts() {
        this.showSpinner = true;
        const dto = { UserId: this.customerId };

        this.subscriptionService.getProducts(dto, this.messageService)
            .then(data => {
                this.showSpinner = false;

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.subscriptionProducts = data.Products;
            })
            .catch(err => {
                this.showSpinner = false;
                console.error(err);
            });
    }

    createOrRemoveSubscriptionCard() {
        this.showSpinner = true;
        const dto = {
            UserId: this.customerId,
            SelectedSubscriptionId: this.selectedSubscriptionProduct?.Id,
            XpressSubscriptionReceiptNumber: this.xpressSubscriptionReceiptNumber
        };
        this.subscriptionService.createOrRemoveSubscriptionCard(dto, this.messageService)
            .then(data => {
                this.showSpinner = false;

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.cancel();
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'User updated' });
                this.displaySubscriptionConfirmDialog = false;
            })
            .catch(err => {
                this.showSpinner = false;
                this.displaySubscriptionConfirmDialog = false;
                console.error(err);
            });
    }

    checkValidation() {
        this.isValidAmount =
            this.adjustWalletBalanceAmount !== null &&
            this.adjustWalletBalanceAmount !== undefined &&
            this.adjustWalletBalanceAmount !== 0;
    }

    adjustWalletBalance() {
        if (this.isAdjustWalletBalanceInFlight || this.isRefundApplyButtonDisabled) {
            return;
        }

        this.isAdjustWalletBalanceInFlight = true;
        this.isRefundButtonDisabled = true;
        this.isRefundApplyButtonDisabled = true;
        const dto = {
            UserId: this.customerId,
            Amount: this.adjustWalletBalanceAmount,
            AdjustWalletBalanceType: this.selectedAdjustWalletBalanceType?.Id,
            Description: this.adjustWalletBalanceDescription
        };
        this.paymentProcessingService.adjustUserWalletBalance(dto, this.messageService, this.adjustWalletBalanceIdempotencyKey)
            .then(data => {
                this.isAdjustWalletBalanceInFlight = false;
                if (!data?.Success) {
                    this.adjustWalletBalanceIdempotencyKey = Utils.newIdempotencyKey();
                    this.isRefundButtonDisabled = false;
                    this.isRefundApplyButtonDisabled = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data?.ErrorMessage ?? 'Failed to adjust wallet balance' });
                    return;
                }
                this.showDialogRefundTransaction = false;
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Adjusted Wallet Balance' });
                this.getUserTransactions(0);
            })
            .catch(err => {
                this.isAdjustWalletBalanceInFlight = false;
                this.isRefundButtonDisabled = false;
                this.isRefundApplyButtonDisabled = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
            });
    }

    showPopupRefundTransaction() {
        if (!this.isAdjustWalletBalanceInFlight) {
            this.isRefundButtonDisabled = false;
            this.isRefundApplyButtonDisabled = false;
            this.adjustWalletBalanceIdempotencyKey = Utils.newIdempotencyKey();
        }

        this.showDialogRefundTransaction = true;
    }

    hideDialogRefundTransaction() {
        this.showDialogRefundTransaction = false;
    }

    updateUserBetaFeatures() {
        const dto = {
            UserId: this.customerId,
            BetaFeatures: this.selectedBetaFeatures.join(';')
        };

        this.xpressUserService.updateUserBetaFeatures(dto, this.messageService).then(data => {
            if (data.Success) {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Beta Features updated' });
            } else {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
            }
        }).catch(err => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
        });
    }

    addServiceTypeInToList() {
        if (this.appDataService.isBoracayLocation) {
            this.serviceTypeList = [
                {
                    name: 'Single',
                    value: ServiceTypeEnum.Single
                },
                {
                    name: 'Charter',
                    value: ServiceTypeEnum.Charter
                },
                {
                    name: 'Scan & Ride',
                    value: ServiceTypeEnum.ScanAndRide
                }
            ];
        } else {
            this.serviceTypeList = [
                {
                    name: 'Ride',
                    value: ServiceTypeEnum.Ride
                }
            ];
        }
    }

    public customerDetailsTab = CustomerDetailsTab;

    public activeTab: CustomerDetailsTab = CustomerDetailsTab.Details;
    public readonly sidebarWidth: string = '1120px';

    public menuItems: MenuItem[] = [
        {
            label: 'Details',
            id: `${CustomerDetailsTab.Details}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Finance',
            id: `${CustomerDetailsTab.Finance}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Order History',
            id: `${CustomerDetailsTab.OrderHistory}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Referrals',
            id: `${CustomerDetailsTab.Referrals}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Insights',
            id: `${CustomerDetailsTab.Insights}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        }
    ];

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        const skip = event.first ? event.first : 0;

        if (event.rows) {
            this.rowsPerPage = event.rows;
        }

        if (!event.sortField) {
            const defaultSortField = this.activeTab === CustomerDetailsTab.Finance ? 'CreatedAt' : 'Id';
            this.sortField = defaultSortField;
            this.sortOrder = -1;
        }
        else {
            this.sortField = event.sortField;
            this.sortOrder = event.sortOrder ? event.sortOrder : 1;
        }

        if (this.activeTab === CustomerDetailsTab.Finance) {
            this.getUserTransactions(skip);
        }
        if (this.activeTab === CustomerDetailsTab.OrderHistory) {
            this.getUserOrdersHistory(skip);
        }
    }

    getInfoUser() {
        const dto = {
            UserId: this.customerId,
            TimeZoneOffset: Utils.getTimezoneOffset
        };
        this.xpressUserService.getInfoUser(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.customer = data;
                this.isBanned = !!data.IsBanned;
                this.isSuspended = !!data.IsSuspended;

                this.banReason = data.BannedReason || '';
                this.banNotes = data.BannedNotes || '';
                this.suspendReason = data.SuspendedReason || '';
                this.suspendNotes = data.SuspendedNotes || '';
                this.customer.UserName = this.formatName(this.customer.UserName);
                this.customer.BirthDate = (this.customer.BirthDate) ? moment(this.customer.BirthDate).format('DD MMMM YYYY') : 'N/A';
                this.customer.SignupDate = (this.customer.SignupDate) ? moment(this.customer.SignupDate).format('DD MMMM YYYY') : 'N/A';
                this.customer.Referred = (this.customer.Referred) ? this.customer.Referred : 'N/A';
                this.customer.Gender = GenderEnum[this.customer.Gender];
                this.customer.SignupType = (this.customer.SignupType) ? this.customer.SignupType : 'N/A';
                this.selectedBetaFeatures = this.customer.BetaFeatures ? this.customer.BetaFeatures.split(';') : [];
            });


    }

    public updatePersonalInfo() {
        this.showSpinner = true;
        const dto = {
            UserId: this.customer.Id,
            FirstName: this.customer.FirstName,
            LastName: this.customer.LastName,
            Email: this.customer.Email,
            Phone: this.customer.Phone
        };
        this.xpressUserService.updatePersonalInfo(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'User info updated' });
                this.cancel();
                this.showEditFields = false;
            })
            .catch(err => {
                this.showSpinner = false;
                throw err;
            });
    }

    getUserTransactions(skip: number) {
        const dto = {
            XpressUserId: this.customer.Id,
            Search: this.filterSearch,
            StartDate: (!this.filterSearchDate) ? null : moment(this.filterSearchDate[0]).format('YYYY-MM-DDTHH:mm:ss'),
            FinishDate: (!this.filterSearchDate) ? null : (!this.filterSearchDate[1]) ? null :
                moment(this.filterSearchDate[1]).format('YYYY-MM-DDTHH:mm:ss'),
            PaymentMethodId: this.filter.transaction,
            IsPaid: this.filter.status,
            TimeZoneOffSet: Utils.getTimezoneOffset,
            Skip: skip,
            Take: this.rowsPerPage,
            SortField: this.sortField,
            SortOrder: this.sortOrder
        };

        const requestKey = JSON.stringify(dto);
        if (this.isTransactionsRequestInFlight && this.lastTransactionsRequestKey === requestKey) {
            return;
        }

        this.showSpinner = true;
        this.isFinanceTransactionsLoading = true;
        this.isTransactionsRequestInFlight = true;
        this.lastTransactionsRequestKey = requestKey;

        this.xpressUserService.getUserTransactions(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.first = skip;
                this.totalRecords = data.TotalRecords;
                this.userAdditionalInfo = data;
                this.userAdditionalInfo.WalletLastTopUpDate = (this.userAdditionalInfo.WalletLastTopUpDate) ?
                    moment(this.userAdditionalInfo.WalletLastTopUpDate).format('DD MMM YYYY') : '';

                if (this.userAdditionalInfo.Transactions) {
                    this.userAdditionalInfo.Transactions = this.userAdditionalInfo.Transactions.map((item: any) => {
                        if (item.Amount > 0) {
                            item.AmountColor = '#1CBA70';
                        } else { item.AmountColor = '#EB4236'; }
                        item.AmountStr = `${item.Amount >= 0 ? '+' : '-'} ₱ ${Math.abs(item.Amount)}`;
                        item.CreatedAtDate = moment(item.CreatedAt).format('DD MMM YYYY');
                        item.CreatedAtTime = moment(item.CreatedAt).format('h:mm A');
                        item.CreatedAtText = `${item.CreatedAtDate} ${item.CreatedAtTime}`;
                        return item;
                    });
                }

                this.userAdditionalInfo.TotalSpend = this.userAdditionalInfo?.TotalSpend.toLocaleString('en-US');
                this.userAdditionalInfo.AvgSpend = this.userAdditionalInfo.AvgSpend.toLocaleString('en-US');
                this.userAdditionalInfo.HighestSpend = this.userAdditionalInfo.HighestSpend.toLocaleString('en-US');
                this.userAdditionalInfo.WalletValue = this.userAdditionalInfo.WalletValue.toLocaleString('en-US');
                this.userAdditionalInfo.WalletLastTopUp = this.userAdditionalInfo.WalletLastTopUp.toLocaleString('en-US');
            })
            .catch(err => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
            })
            .finally(() => {
                this.showSpinner = false;
                this.isFinanceTransactionsLoading = false;
                this.isTransactionsRequestInFlight = false;
            });
    }

    public getUserOrdersHistory(skip: number) {
        this.showSpinner = true;
        const dto = {
            XpressUserId: this.customer.Id,
            Search: this.filterSearch,
            StartDate: (!this.filterSearchDate) ? null : moment(this.filterSearchDate[0]).format('YYYY-MM-DDTHH:mm:ss'),
            FinishDate: (!this.filterSearchDate) ? null : (!this.filterSearchDate[1]) ? null :
                moment(this.filterSearchDate[1]).format('YYYY-MM-DDTHH:mm:ss'),
            IsPaid: this.filter.status,
            ServiceTypes: this.selectedServiceTypes,
            TimeZoneOffSet: Utils.getTimezoneOffset(),
            Skip: skip,
            Take: this.rowsPerPage,
            SortField: this.sortField,
            SortOrder: this.sortOrder
        };
        this.xpressUserService.getUserOrdersHistory(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.first = skip;
                this.totalRecords = data.TotalRecords;
                this.userAdditionalInfo = data;

                this.userAdditionalInfo.OrdersHistory.forEach((item: any) => {
                    if (item.Value > 0) {
                        item.ValueColor = '#1CBA70';
                    } else { item.ValueColor = '#EB4236'; }
                    item.ValueStr = `${item.Value >= 0 ? '+' : '-'} ₱ ${Math.abs(item.Value)}`;
                    item.CreatedAtDate = moment(item.CreatedAt).format('DD MMM YYYY');
                    item.CreatedAtTime = moment(item.CreatedAt).format('h:mm A');
                    item.BookingId = (item.Id) ? `XP${item.Id}` : 'N/A';
                });

                this.showSpinner = false;
            })
            .catch(err => {
                this.showSpinner = false;
                throw err;
            });
    }

    public getUserRefferals() {
        this.showSpinner = true;
        const dto = {
            XpressUserId: this.customer.Id,
            TimeZoneOffSet: Utils.getTimezoneOffset
        };
        this.xpressUserService.getUserRefferals(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.userAdditionalInfo = data;
                this.userAdditionalInfo.TotalSpendUsers = `₱ ${this.userAdditionalInfo.TotalSpendUsers.toLocaleString('en-US',
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                this.userAdditionalInfo.Transactions.forEach((item: any) => {
                    item.CreatedAtDate = moment(item.CreatedAt).format('DD MMM YYYY');
                    item.CreatedAtTime = moment(item.CreatedAt).format('h:mm A');
                    item.TotalSpend = `₱ ${item.TotalSpend.toLocaleString('en-US',
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                });
                this.showSpinner = false;
            })
            .catch(err => {
                this.showSpinner = false;
                throw err;
            });
    }

    public getUserAnalytics() {
        this.showSpinner = true;
        const dto = {
            XpressUserId: this.customer.Id,
            TimeZoneOffSet: Utils.getTimezoneOffset
        };
        this.xpressUserService.getUserAnalytics(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.userAdditionalInfo = data;
                this.userAdditionalInfo.MostUsedServiceRide = (data.MostUsedServiceRide !== 'None') ? data.MostUsedServiceRide : 'N/A';
                this.userAdditionalInfo.MostUsedServiceDelivery = (this.userAdditionalInfo.MostUsedServiceDelivery &&
                    this.userAdditionalInfo.MostUsedServiceDelivery !== 'None')
                    ? this.userAdditionalInfo.MostUsedServiceDelivery
                    : 'N/A';
                this.userAdditionalInfo.AvgBookingPreWeekDelivery = this.userAdditionalInfo.AvgBookingPreWeekDelivery ?
                    `₱ ${this.userAdditionalInfo.TotalAvgBookingPreWeekDeliveryRide.toFixed(2).toLocaleString('en-US')}`
                    : 'N/A';
                this.userAdditionalInfo.TotalDelivery = `₱ ${this.userAdditionalInfo.TotalDelivery.toFixed(2).toLocaleString('en-US')}`;
                this.userAdditionalInfo.TotalRide = this.userAdditionalInfo.TotalRide ?
                    `₱ ${this.userAdditionalInfo.TotalRide.toFixed(2).toLocaleString('en-US')}`
                    : 'N/A';
                this.userAdditionalInfo.UsualBookingTimeRide = data.UsualBookingTimeRide ?
                    moment(data.UsualBookingTimeRide).format('h:mmA') : 'N/A';
                this.userAdditionalInfo.LastRide = data.LastRide ? Utils.utcToLocaleString(data.LastRide) : 'Never';
                this.userAdditionalInfo.LastFood = data.LastFood ? Utils.utcToLocaleString(data.LastFood) : 'Never';
                this.userAdditionalInfo.LastHire = data.LastHire ? Utils.utcToLocaleString(data.LastHire) : 'Never';
                this.userAdditionalInfo.LastDelivery = data.LastDelivery ? Utils.utcToLocaleString(data.LastDelivery) : 'Never';
                this.userAdditionalInfo.TotalSpendPayment = `₱ ${this.userAdditionalInfo.TotalSpendPayment.toFixed(2).toLocaleString('en-US')}`;
                this.userAdditionalInfo.PercentageIncrease = data.PercentageIncrease >= 0 ? `+ ${data.PercentageIncrease}` : `- ${data.PercentageIncrease}`;
                this.showSpinner = false;
            })
            .catch(err => {
                this.showSpinner = false;
                throw err;
            });
    }

    public getUserPromos() {
        this.showSpinner = true;
        const dto = {
            XpressUserId: this.customer.Id
        };
        this.xpressUserService.getUserPromos(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.userAdditionalInfo = data.Promos;
                this.showSpinner = false;
            })
            .catch(err => {
                this.showSpinner = false;
                throw err;
            });
    }

    public getUserShedule() {
        this.showSpinner = true;
        const dto = {
            XpressUserId: this.customer.Id
        };
        this.xpressUserService.getUserShedule(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.userAdditionalInfo = data.Data;
                this.showSpinner = false;
            })
            .catch(err => {
                this.showSpinner = false;
                throw err;
            });
    }

    private callMethods() {
        this.sortField = '';
        this.sortOrder = 1;
        this.totalRecords = -1;
        this.first = 0;

        if (this.activeTab === CustomerDetailsTab.Finance) {
            this.userAdditionalInfo = undefined;
            this.handleLazyLoad({
                first: 0,
                rows: this.rowsPerPage
            });
        }
        else if (this.activeTab === CustomerDetailsTab.OrderHistory) {
            this.userAdditionalInfo = undefined;
            this.handleLazyLoad({
                first: 0,
                rows: this.rowsPerPage
            });
        }
        else if (this.activeTab === CustomerDetailsTab.Referrals) {
            this.userAdditionalInfo = undefined;
            this.getUserRefferals();
        }
        else if (this.activeTab === CustomerDetailsTab.Insights) {
            this.userAdditionalInfo = undefined;
            this.getUserAnalytics();
        }
    }

    public handleTabChange(tab: CustomerDetailsTab) {
        this.activeTab = tab;
        this.callMethods();
    }

    public setShowEditFields() {
        this.showEditFields = true;
    }

    public handleFilterChange(): void {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.callMethods();
            this.searchTimer = 0;
        }, 1500);
    }

    public showModalSuspend(user: any) {
        this.suspendUser = {
            userId: user.Id,
            userName: user.UserName
        };
        this.isLiftSuspensionAction = false;
        this.displayModalSuspend = !this.displayModalSuspend;
    }

    public showModalLiftSuspension(user: any) {
        this.suspendUser = {
            userId: user.Id,
            userName: user.UserName
        };
        this.isLiftSuspensionAction = true;
        this.displayModalSuspend = !this.displayModalSuspend;
    }

    changedDisplayModalSuspendEmit(value: any) {
        this.displayModalSuspend = value;
    }

    onSuspendSuccess() {
        this.isSuspended = true;
        this.suspendSuccessEmit.emit();
    }

    onLiftSuspensionSuccess() {
        this.isSuspended = false;
        this.liftSuspensionSuccessEmit.emit();
    }

    public showModalBanned(user: any) {
        this.bannedUser = {
            userId: user.Id,
            userName: user.UserName
        };
        this.isLiftBanAction = this.isBanned;

        this.displayModalBanned = !this.displayModalBanned;
    }

    changedDisplayModalBannedEmit(obj: any) {
        this.displayModalBanned = obj.displayModalBanned;
    }

    onBanSuccess() {
        this.isBanned = true;
        this.isSuspended = false;
        this.banSuccessEmit.emit();
    }

    onLiftBanSuccess() {
        this.isBanned = false;
        this.liftBanSuccessEmit.emit();
    }

    public showCustomerTransactionDetail(transaction: any) {
        this.transactionId = transaction.Id;
        this.isVisibleCustomerTransactionDetail = !this.isVisibleCustomerTransactionDetail;
    }

    public showCustomerOrderDetail(transactionId: any) {
        this.orderTransactionId = transactionId;
        this.isVisibleCustomerOrderDetail = !this.isVisibleCustomerOrderDetail;
    }

    changedVisibleCustomerTransactionDetailEmit(value: any) {
        this.isVisibleCustomerTransactionDetail = value;
    }

    changedVisibleCustomerOrderDetailEmit(value: any) {
        this.isVisibleCustomerOrderDetail = value;
    }

    public openSidebarDisplayOrderDetail() {
        this.isVisibleDisplayOrderDetail = true;
    }
    changedVisibleDisplayOrderDetailEmit(value: any) {
        this.isVisibleDisplayOrderDetail = value;
    }

    changeFilterStatus(status?: boolean) {
        this.filter.status = status ?? null;
    }

    clickClearFilter() {
        this.filter.transaction = null;
        this.filter.status = null;
        this.selectedServiceTypes = [];
    }

    formatName(name: string): string {
        const [firstName, ...initials] = name.split(' ');
        return `${firstName} ${initials.map(initial => `${initial.charAt(0)}.`).join(' ')}`;
    }

    cancel() {
        this.changedVisibleCustomerInfo(false);
    }
    addOrRemoveUserToCampany() {
        this.showSpinner = true;
        const dto = {
            UserId: this.customerId
        };
        this.campaignUsersService.addOrRemoveUserToCampany(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.showSpinner = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.cancel();
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'User updated' });
                this.showSpinner = false;
                this.displayConfirmDialog = true;
            })
            .catch(err => {
                this.showSpinner = false;
                this.displayConfirmDialog = false;
                throw err;
            });
    }

    showSubscriptionConfirmDialog() {
        this.displaySubscriptionConfirmDialog = !this.displaySubscriptionConfirmDialog;
    }

    hideSubscriptionConfirmDialog() {
        this.displaySubscriptionConfirmDialog = !this.displaySubscriptionConfirmDialog;
        this.xpressSubscriptionReceiptNumber = '';
    }

    showConfirmDialog() {
        this.displayConfirmDialog = !this.displayConfirmDialog;
    }

    changeConfirmDialogVisibility() {
        this.displayConfirmDialog = !this.displayConfirmDialog;
    }

    confirmAction() {
        this.addOrRemoveUserToCampany();
    }

    showManageDiscountDialog() {
        this.visibleCustomerManageDiscountDialog = true;
    }

    changedVisibleCustomerManageDiscountDialogEmit(obj: any) {
        this.visibleCustomerManageDiscountDialog = obj.visibleCustomerManageDiscountDialog;
        this.customer.SpecialDiscountType = obj.specialDiscountType;
        this.customer.SpecialDiscountStatus = obj.specialDiscountStatus;
    }
}
